import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchProductsByCollection, fetchShopifyCollections, fetchShopifyProducts } from "@/lib/shopify";
import type { ShopifyProduct } from "@/lib/shopify";
import ShopifyProductCard from "@/components/ShopifyProductCard";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import PageTransition from "@/components/PageTransition";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SlidersHorizontal, X, ChevronLeft, Home } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import CategoryFilter from "@/components/CategoryFilter";
import { SEO, SITE_URL } from "@/components/SEO";

const Category = () => {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const [mainProducts, setMainProducts] = useState<ShopifyProduct[]>([]);
  const [additionalProducts, setAdditionalProducts] = useState<Map<string, ShopifyProduct[]>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isLoadingAdditional, setIsLoadingAdditional] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [maxPrice, setMaxPrice] = useState<number>(1000);
  const [minPrice, setMinPrice] = useState<number>(0);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [categoryTitle, setCategoryTitle] = useState<string>("");
  const [collections, setCollections] = useState<Array<{ node: { id: string; title: string; handle: string } }>>([]);
  const [filteredCollections, setFilteredCollections] = useState<Array<{ node: { id: string; title: string; handle: string } }>>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [openCategories, setOpenCategories] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'alpha-asc' | 'alpha-desc' | 'price-asc' | 'price-desc'>('newest');

  useEffect(() => {
    const loadCollections = async () => {
      try {
        // Fetch all collections with pagination
        let allCollections: Array<{ node: { id: string; title: string; handle: string } }> = [];
        let hasNextPage = true;
        let endCursor: string | null = null;
        
        while (hasNextPage) {
          const result = await fetchShopifyCollections(250, endCursor);
          allCollections = [...allCollections, ...result.collections];
          hasNextPage = result.hasNextPage;
          endCursor = result.endCursor;
        }
        
        setCollections(allCollections);

        // Load category filter settings from database
        const { data: categorySetting } = await supabase
          .from('site_settings')
          .select('setting_value')
          .eq('setting_key', 'category_filter_collections')
          .maybeSingle();

        if (categorySetting?.setting_value) {
          const value = categorySetting.setting_value as { handles?: string[] };
          if (value.handles && value.handles.length > 0) {
            // Filter and preserve the order from database settings
            const filtered = value.handles
              .map(handle => allCollections.find(c => c.node.handle === handle))
              .filter((c): c is typeof allCollections[0] => c !== undefined);
            setFilteredCollections(filtered);
            // Set open categories from the filtered list
            setOpenCategories(value.handles);
          } else {
            setFilteredCollections(allCollections);
            setOpenCategories(allCollections.map(c => c.node.handle));
          }
        } else {
          setFilteredCollections(allCollections);
          setOpenCategories(allCollections.map(c => c.node.handle));
        }
      } catch (error) {
        console.error('Error loading collections:', error);
      }
    };
    loadCollections();
  }, []);

  // Update category title when collections are loaded
  useEffect(() => {
    if (!category || collections.length === 0) return;
    
    if (category === 'all') {
      setCategoryTitle("כל המוצרים");
    } else {
      const currentCollection = collections.find(c => c.node.handle === category);
      if (currentCollection) {
        setCategoryTitle(currentCollection.node.title);
      }
    }
  }, [category, collections]);

  useEffect(() => {
    const loadProducts = async () => {
      if (!category) return;
      
      setIsLoading(true);
      setSelectedCategories([]);
      setAdditionalProducts(new Map());
      try {
        // Check if we're loading all products
        if (category === 'all') {
          setCategoryTitle("כל המוצרים");
          
          // Fetch all products (not from a specific collection)
          const { edges, pageInfo } = await fetchShopifyProducts(50);
          const productsArray = edges.map(p => p.node);
          setMainProducts(productsArray);
          setHasNextPage(pageInfo.hasNextPage);
          setEndCursor(pageInfo.endCursor);
          
          // Calculate min and max price from products
          if (productsArray.length > 0) {
            const prices = productsArray.map(p => parseFloat(p.priceRange.minVariantPrice.amount));
            const minProductPrice = Math.min(...prices);
            const maxProductPrice = Math.max(...prices);
            const roundedMin = Math.floor(minProductPrice / 10) * 10;
            const roundedMax = Math.ceil(maxProductPrice / 10) * 10;
            setMinPrice(roundedMin);
            setMaxPrice(roundedMax);
            setPriceRange([roundedMin, roundedMax]);
          }
        } else {
          // Fetch products from this specific collection
          const { edges, pageInfo } = await fetchProductsByCollection(category, 50);
          const productsArray = edges.map(p => p.node);
          setMainProducts(productsArray);
          setHasNextPage(pageInfo.hasNextPage);
          setEndCursor(pageInfo.endCursor);
          
          // Calculate min and max price from products
          if (productsArray.length > 0) {
            const prices = productsArray.map(p => parseFloat(p.priceRange.minVariantPrice.amount));
            const minProductPrice = Math.min(...prices);
            const maxProductPrice = Math.max(...prices);
            const roundedMin = Math.floor(minProductPrice / 10) * 10;
            const roundedMax = Math.ceil(maxProductPrice / 10) * 10;
            setMinPrice(roundedMin);
            setMaxPrice(roundedMax);
            setPriceRange([roundedMin, roundedMax]);
          }
        }
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, [category]);

  // Load products from additional selected categories
  useEffect(() => {
    const loadAdditionalProducts = async () => {
      if (selectedCategories.length === 0) {
        setAdditionalProducts(new Map());
        return;
      }

      setIsLoadingAdditional(true);
      const newAdditionalProducts = new Map<string, ShopifyProduct[]>();

      try {
        for (const categoryHandle of selectedCategories) {
          if (categoryHandle === category) continue;
          
          const { edges } = await fetchProductsByCollection(categoryHandle, 50);
          const productsArray = edges.map(p => p.node);
          newAdditionalProducts.set(categoryHandle, productsArray);
        }
        setAdditionalProducts(newAdditionalProducts);
      } catch (error) {
        console.error('Error loading additional products:', error);
      } finally {
        setIsLoadingAdditional(false);
      }
    };

    loadAdditionalProducts();
  }, [selectedCategories, category]);

  const loadMoreProducts = async () => {
    if (!category || !hasNextPage || !endCursor || isLoadingMore) return;
    
    setIsLoadingMore(true);
    try {
      let edges, pageInfo;
      
      if (category === 'all') {
        // Load more from all products
        const result = await fetchShopifyProducts(50, endCursor);
        edges = result.edges;
        pageInfo = result.pageInfo;
      } else {
        // Load more from specific collection
        const result = await fetchProductsByCollection(category, 50, endCursor);
        edges = result.edges;
        pageInfo = result.pageInfo;
      }
      
      const newProducts = edges.map(p => p.node);
      setMainProducts(prev => [...prev, ...newProducts]);
      setHasNextPage(pageInfo.hasNextPage);
      setEndCursor(pageInfo.endCursor);
      
      // Update max price if needed
      if (newProducts.length > 0) {
        const allProducts = [...mainProducts, ...newProducts];
        const maxProductPrice = Math.max(
          ...allProducts.map(p => parseFloat(p.priceRange.minVariantPrice.amount))
        );
        const roundedMax = Math.ceil(maxProductPrice / 10) * 10;
        if (roundedMax > maxPrice) {
          setMaxPrice(roundedMax);
          setPriceRange([priceRange[0], roundedMax]);
        }
      }
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const sortProducts = (products: ShopifyProduct[]) => {
    const items = [...products];
    items.sort((a, b) => {
      switch (sortOrder) {
        case 'newest':
          return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        case 'oldest':
          return new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();
        case 'alpha-asc':
          return a.title.localeCompare(b.title, 'he');
        case 'alpha-desc':
          return b.title.localeCompare(a.title, 'he');
        case 'price-asc':
          return (
            parseFloat(a.priceRange.minVariantPrice.amount) -
            parseFloat(b.priceRange.minVariantPrice.amount)
          );
        case 'price-desc':
          return (
            parseFloat(b.priceRange.minVariantPrice.amount) -
            parseFloat(a.priceRange.minVariantPrice.amount)
          );
        default:
          return 0;
      }
    });
    return items;
  };

  // IMPORTANT:
  // - לא מזיזים את סדר הקטגוריות בתפריט.
  // - כן מזיזים את סדר התצוגה של המוצרים כך שהקטגוריה שסימנת עכשיו תופיע למעלה (LIFO).
  // - בתוך כל קטגוריה עדיין נשמר המיון שנבחר (חדש/ישן/מחיר/א-ת).
  const filteredProducts = (() => {
    const result: ShopifyProduct[] = [];
    const addedIds = new Set<string>();

    const pushGroup = (groupProducts: ShopifyProduct[]) => {
      const inPriceRange = groupProducts.filter((product) => {
        const price = parseFloat(product.priceRange.minVariantPrice.amount);
        return price >= priceRange[0] && price <= priceRange[1];
      });

      const sorted = sortProducts(inPriceRange);
      for (const product of sorted) {
        if (!addedIds.has(product.id)) {
          result.push(product);
          addedIds.add(product.id);
        }
      }
    };

    // Last selected category first (LIFO)
    const reversedCategories = [...selectedCategories].reverse();
    for (const categoryHandle of reversedCategories) {
      if (categoryHandle === category) continue;
      pushGroup(additionalProducts.get(categoryHandle) || []);
    }

    // Then main category (route)
    pushGroup(mainProducts);

    return result;
  })();

  const handleClearFilters = async () => {
    setPriceRange([minPrice, maxPrice]);
    setSelectedCategories([]);
    setAdditionalProducts(new Map());
    setSortOrder('newest');
    
    // Navigate to all products and reload
    navigate('/collections/all');
    
    setIsLoading(true);
    try {
      setCategoryTitle("כל המוצרים");
      
      const { edges, pageInfo } = await fetchShopifyProducts(50);
      const productsArray = edges.map(p => p.node);
      setMainProducts(productsArray);
      setHasNextPage(pageInfo.hasNextPage);
      setEndCursor(pageInfo.endCursor);
      
      if (productsArray.length > 0) {
        const prices = productsArray.map(p => parseFloat(p.priceRange.minVariantPrice.amount));
        const minProductPrice = Math.min(...prices);
        const maxProductPrice = Math.max(...prices);
        const roundedMin = Math.floor(minProductPrice / 10) * 10;
        const roundedMax = Math.ceil(maxProductPrice / 10) * 10;
        setMinPrice(roundedMin);
        setMaxPrice(roundedMax);
        setPriceRange([roundedMin, roundedMax]);
      }
    } catch (error) {
      console.error('Error reloading products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCategorySelection = (handle: string) => {
    // Toggle category selection - always add/remove from selectedCategories
    setSelectedCategories(prev => {
      const isCurrentlySelected = prev.includes(handle);
      const newSelection = isCurrentlySelected 
        ? prev.filter(h => h !== handle)
        : [...prev, handle];
      
      return newSelection;
    });
    
    // Close filter sheet on mobile after selection
    setTimeout(() => {
      setIsFilterOpen(false);
    }, 150);
  };

  // Determine navigation state
  const hasPriceFilter = priceRange[0] !== minPrice || priceRange[1] !== maxPrice;
  const hasMultipleCategories = selectedCategories.length > 1;
  const hasSingleCategoryFilter = selectedCategories.length === 1;
  const isCustomFilter = hasMultipleCategories || hasPriceFilter || (hasSingleCategoryFilter && selectedCategories[0] !== category);
  
  // Get the active category for header highlighting
  const getActiveCategory = (): string | null => {
    if (hasMultipleCategories || hasPriceFilter) {
      // Multiple filters - no header highlight
      return null;
    }
    if (hasSingleCategoryFilter) {
      // Single category selected - highlight that one
      return selectedCategories[0];
    }
    // No filters - highlight current URL category
    return category === 'all' ? null : category || null;
  };

  const activeCategory = getActiveCategory();

  // Display title logic
  const getDisplayTitle = () => {
    if (isCustomFilter) {
      return "סינון מותאם אישית";
    }
    if (hasSingleCategoryFilter) {
      const selectedCollection = collections.find(c => c.node.handle === selectedCategories[0]);
      return selectedCollection?.node.title || categoryTitle || "מוצרים";
    }
    return categoryTitle || "מוצרים";
  };
  
  const displayTitle = getDisplayTitle();

  // Get the title for the current main category
  const getCurrentCategoryTitle = () => {
    if (category === 'all') return "כל המוצרים";
    const currentCollection = collections.find(c => c.node.handle === category);
    return currentCollection?.node.title || categoryTitle;
  };

  // Prepare SEO data with category-specific descriptions
  const getCategoryDescription = (handle: string, title: string) => {
    const descriptions: Record<string, string> = {
      'עגילים': 'עגילי זהב עגילים היו עוד מימי הביניים הדרך של נשים לייפות את עצמן ולהרגיש יפות. מגוון ענק של עגילי זהב באליס תכשיטים.',
      'שרשראות': 'שרשראות זהב לרכוש מתנה לאדם אהוב הוא משימה מאוד מורכבת. ישנו מבחר ענק של שרשראות עם תליון באליס תכשיטים.',
      'טבעות': 'מגוון רחב של טבעות מזהב טהור 14 קראט. טבעות אירוסין, טבעות נישואין וטבעות מעוצבות באליס תכשיטים.',
      'צמידים': 'צמידי זהב צמיד הוא בין התכשיטים המועדפים על שני המינים גם יחד. מבחר צמידי זהב יוקרתיים באליס תכשיטים.',
    };
    return descriptions[title] || `${title} - קולקציית תכשיטים יוקרתיים מבית אליס תכשיטים באר שבע`;
  };

  const seoTitle = displayTitle ? `${displayTitle} אליס` : 'קולקציות תכשיטים';
  const seoDescription = category === 'all' 
    ? 'כל המוצרים בחנות אליס תכשיטים - מגוון רחב של תכשיטים ושעונים יוקרתיים. טבעות אירוסין, שרשראות זהב, עגילים וצמידים.'
    : getCategoryDescription(category || '', displayTitle);
  const canonicalUrl = `${SITE_URL}/collections/${category}`;

  return (
    <PageTransition>
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalUrl}
        breadcrumbs={[
          { name: 'ראשי', url: '/' },
          { name: seoTitle, url: `/collections/${category}` },
        ]}
      />
      <div className="min-h-screen bg-white" dir="rtl">
        <Header activeCategoryHandle={activeCategory} />
      <main className="py-12 px-[25px] md:px-[45px] lg:px-[100px]">
        <div className="mx-auto">
          {/* Navigation Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-6 text-muted-foreground animate-fade-in">
            <a href="/" className="hover:text-primary transition-colors flex items-center gap-1">
              <Home className="h-4 w-4" />
              <span className="hidden sm:inline">דף הבית</span>
            </a>
            <ChevronLeft className="h-4 w-4" />
            {isCustomFilter ? (
              <div className="flex items-center gap-2">
                <span className="text-foreground">{displayTitle}</span>
                <button
                  onClick={handleClearFilters}
                  className="flex items-center justify-center w-5 h-5 rounded-full bg-muted hover:bg-destructive hover:text-white transition-colors"
                  title="נקה סינון"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <span className="text-foreground">{getCurrentCategoryTitle()}</span>
            )}
          </nav>

          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-3xl font-light mb-3 tracking-wide" style={{ fontWeight: 300 }}>
              {displayTitle}
            </h1>
            <div className="w-12 h-px bg-primary/40 mx-auto"></div>
          </div>

          {/* Mobile Filter Button */}
          <div className="lg:hidden mb-6">
            <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="w-full">
                  <SlidersHorizontal className="ml-2 h-4 w-4" />
                  סינון מוצרים
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-[400px] flex flex-col h-full p-0">
                {/* Price Filter - Fixed at Top */}
                <div className="flex-shrink-0 p-6 pb-4 border-b border-border/30">
                  {/* Sort Order */}
                  <div className="mb-6">
                    <h3 className="text-primary text-sm mb-3" style={{ fontWeight: 400 }}>
                      מיון לפי
                    </h3>
                  <Select value={sortOrder} onValueChange={(value: 'newest' | 'oldest' | 'alpha-asc' | 'alpha-desc' | 'price-asc' | 'price-desc') => setSortOrder(value)}>
                      <SelectTrigger className="w-full text-right">
                        <SelectValue placeholder="בחר מיון" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="newest">מהחדש לישן</SelectItem>
                        <SelectItem value="oldest">מהישן לחדש</SelectItem>
                        <SelectItem value="price-asc">מחיר: מהנמוך לגבוה</SelectItem>
                        <SelectItem value="price-desc">מחיר: מהגבוה לנמוך</SelectItem>
                        <SelectItem value="alpha-asc">מא׳ עד ת׳</SelectItem>
                        <SelectItem value="alpha-desc">מת׳ עד א׳</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <h3 className="text-primary text-sm mb-6" style={{ fontWeight: 400 }}>
                    סינון לפי טווח מחירים
                  </h3>
                  <Slider
                    value={priceRange}
                    onValueChange={(value) => setPriceRange(value as [number, number])}
                    min={minPrice}
                    max={maxPrice}
                    step={10}
                    className="mb-4"
                  />
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>₪{priceRange[0]}</span>
                    <span>₪{priceRange[1]}</span>
                  </div>
                </div>

                {/* Categories Filter - Scrollable */}
                <div className="flex-1 overflow-y-auto p-6 pt-4">
                  <h3 className="text-primary text-sm mb-4" style={{ fontWeight: 400 }}>
                    קטגוריות נוספות
                  </h3>
                  <CategoryFilter
                    collections={collections}
                    selectedCategories={selectedCategories}
                    currentCategory={category}
                    onToggleCategory={toggleCategorySelection}
                    openCategories={openCategories}
                  />
                </div>

                {/* Clear Filters Button - Fixed at Bottom */}
                <div className="flex-shrink-0 p-6 pt-4 border-t border-border/30">
                  <Button
                    onClick={() => {
                      handleClearFilters();
                      setIsFilterOpen(false);
                    }}
                    className="w-full bg-primary/90 hover:bg-primary text-white"
                    style={{ fontWeight: 300, fontSize: '14px' }}
                  >
                    נקה סינון
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex flex-col lg:flex-row-reverse gap-8">
            {/* Products Grid */}
            <div className="flex-1">
              {(isLoading || isLoadingAdditional) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 min-[1650px]:grid-cols-5 gap-8">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="aspect-square bg-muted mb-4" />
                      <div className="h-3 bg-muted mb-2" />
                      <div className="h-3 bg-muted w-1/2" />
                    </div>
                  ))}
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="text-center py-24 animate-fade-in">
                  <p className="text-muted-foreground text-base" style={{ fontWeight: 300 }}>
                    לא נמצאו מוצרים
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 min-[1650px]:grid-cols-5 gap-8">
                    {filteredProducts.map((product, index) => (
                      <div 
                        key={product.id}
                        className="animate-fade-in"
                        style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'backwards' }}
                      >
                        <ShopifyProductCard product={product} collectionHandle={category} />
                      </div>
                    ))}
                  </div>
                  
                  {hasNextPage && (
                    <div className="flex justify-center mt-12">
                      <Button
                        onClick={loadMoreProducts}
                        disabled={isLoadingMore}
                        className="bg-primary/90 hover:bg-primary text-white px-12 py-6"
                        style={{ fontWeight: 300 }}
                      >
                        {isLoadingMore ? 'טוען...' : 'טען עוד מוצרים'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Filters Sidebar - Desktop Only */}
            <div className="hidden lg:block w-80 animate-fade-in" style={{ animationDelay: '0.1s', animationFillMode: 'backwards' }}>
              <div className="border border-border/30 p-6 sticky top-24 text-right space-y-6">
                {/* Sort Order */}
                <div>
                  <h3 className="text-primary text-sm mb-3" style={{ fontWeight: 400 }}>
                    מיון לפי
                  </h3>
                  <Select value={sortOrder} onValueChange={(value: 'newest' | 'oldest' | 'alpha-asc' | 'alpha-desc' | 'price-asc' | 'price-desc') => setSortOrder(value)}>
                    <SelectTrigger className="w-full text-right">
                      <SelectValue placeholder="בחר מיון" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">מהחדש לישן</SelectItem>
                      <SelectItem value="oldest">מהישן לחדש</SelectItem>
                      <SelectItem value="price-asc">מחיר: מהנמוך לגבוה</SelectItem>
                      <SelectItem value="price-desc">מחיר: מהגבוה לנמוך</SelectItem>
                      <SelectItem value="alpha-asc">מא׳ עד ת׳</SelectItem>
                      <SelectItem value="alpha-desc">מת׳ עד א׳</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Price Filter - Always Visible */}
                <div>
                  <h3 className="text-primary text-sm mb-6" style={{ fontWeight: 400 }}>
                    סינון לפי טווח מחירים
                  </h3>
                  <Slider
                    value={priceRange}
                    onValueChange={(value) => setPriceRange(value as [number, number])}
                    min={minPrice}
                    max={maxPrice}
                    step={10}
                    className="mb-4"
                  />
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>₪{priceRange[0]}</span>
                    <span>₪{priceRange[1]}</span>
                  </div>
                </div>

                {/* Categories Filter - Scrollable */}
                <div>
                  <h3 className="text-primary text-sm mb-4" style={{ fontWeight: 400 }}>
                    קטגוריות נוספות
                  </h3>
                  <div className="max-h-[calc(100vh-420px)] overflow-y-auto">
                    <CategoryFilter
                      collections={collections}
                      selectedCategories={selectedCategories}
                      currentCategory={category}
                      onToggleCategory={toggleCategorySelection}
                      openCategories={openCategories}
                    />
                  </div>
                </div>

                {/* Clear Filters Button */}
                <Button
                  onClick={handleClearFilters}
                  className="w-full bg-primary/90 hover:bg-primary text-white"
                  style={{ fontWeight: 300, fontSize: '14px' }}
                >
                  נקה סינון
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer2 />
    </div>
    </PageTransition>
  );
};

export default Category;
